import { isNumericFieldType } from '@/utils/field-value'
import type { PositionCellLabels, PositionCellValue, PositionColumn, PositionValue, PredefinedPosition } from './types'

export function visiblePositions(keys: PredefinedPosition[], values: PositionValue[]): PositionValue[] {
  const storedByKey = new Map(values.map((value) => [value.key, value]))

  const predefined = keys
    .filter((position) => storedByKey.has(position.key))
    .map((position) => ({
      key: position.key,
      label: position.label,
      value: storedByKey.get(position.key)?.value ?? null,
      custom: false,
      icon: position.icon,
    }))

  return [...predefined, ...values.filter((value) => value.custom)]
}

export function unusedPositions(keys: PredefinedPosition[], usedKeys: Set<string>): PredefinedPosition[] {
  return keys.filter((position) => !usedKeys.has(position.key))
}

export function groupByHeading(
  positions: PredefinedPosition[],
): { group: string | null; positions: PredefinedPosition[] }[] {
  if (!positions.some((position) => position.group)) {
    return [{ group: null, positions }]
  }

  const groups = new Map<string, PredefinedPosition[]>()

  positions.forEach((position) => {
    const group = position.group ?? ''

    groups.set(group, [...(groups.get(group) ?? []), position])
  })

  return [...groups.entries()].map(([group, grouped]) => ({ group: group || null, positions: grouped }))
}

export function matchingSuggestion(column: PositionColumn, value: PositionCellValue): PredefinedPosition | null {
  const label = String(value ?? '').toLowerCase()

  return (column.suggestions ?? []).find((position) => position.label.toLowerCase() === label) ?? null
}

export function isNumericColumn(column: PositionColumn): boolean {
  return isNumericFieldType(column.type)
}

interface CellFormatOptions {
  locale: string
  labels: PositionCellLabels
  maxFractionDigits: number
}

export function formatCell(
  column: PositionColumn,
  value: PositionCellValue,
  { locale, labels, maxFractionDigits }: CellFormatOptions,
): string {
  if (value === null || value === undefined || value === '') {
    return labels.empty
  }

  if (column.type === 'boolean') {
    return value ? labels.yes : labels.no
  }

  if (isNumericColumn(column)) {
    const number = new Intl.NumberFormat(locale, {
      style: column.type === 'currency' ? 'currency' : 'decimal',
      currency: column.type === 'currency' ? 'EUR' : undefined,
      minimumFractionDigits: column.fractionDigits ?? 0,
      maximumFractionDigits: column.fractionDigits ?? maxFractionDigits,
      useGrouping: column.useGrouping ?? true,
    }).format(Number(value))

    if (column.type === 'currency') {
      return number
    }

    return column.type === 'percent' ? `${number} %` : `${number}${column.suffix ?? ''}`
  }

  if (column.type === 'select') {
    return column.options?.find((option) => option.value === value)?.label ?? String(value)
  }

  return String(value)
}

export function formatTotal(
  total: number,
  {
    locale,
    unit,
    minFractionDigits,
    maxFractionDigits,
  }: {
    locale: string
    unit: string
    minFractionDigits: number
    maxFractionDigits: number
  },
): string {
  const isCurrency = unit === '€'

  const number = new Intl.NumberFormat(locale, {
    style: isCurrency ? 'currency' : 'decimal',
    currency: isCurrency ? 'EUR' : undefined,
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  }).format(total)

  return isCurrency ? number : `${number} ${unit}`
}
